<template>
  <b-card class="ins-base-value-component">
    <h1>
      Insurance Base Value
      <span v-if="hasData" class="total-badge">(Total: {{ formatMoney(summary.total) }})</span>
    </h1>

    <b-row v-if="hasData" class="mb-3 summary-row">
      <b-col cols="6" md="3" class="mb-2">
        <div class="summary-box">
          <span class="summary-label">Total count</span>
          <strong class="summary-value">{{ summary.count }}</strong>
        </div>
      </b-col>
      <b-col cols="6" md="3" class="mb-2">
        <div class="summary-box">
          <span class="summary-label">NP</span>
          <strong class="summary-value">{{ summary.np.count }}</strong>
          <span class="summary-sub">{{ formatMoney(summary.np.total) }}</span>
        </div>
      </b-col>
      <b-col cols="6" md="3" class="mb-2">
        <div class="summary-box">
          <span class="summary-label">NC</span>
          <strong class="summary-value">{{ summary.nc.count }}</strong>
          <span class="summary-sub">{{ formatMoney(summary.nc.total) }}</span>
        </div>
      </b-col>
      <b-col cols="6" md="3" class="mb-2">
        <div class="summary-box">
          <span class="summary-label">Grand total</span>
          <strong class="summary-value">{{ formatMoney(summary.total) }}</strong>
        </div>
      </b-col>
    </b-row>

    <div class="ins-base-chart-container">
      <vue-apex-charts
        v-if="hasData"
        :options="chartOptions"
        :series="series"
        type="bar"
        height="380"
      />
      <p v-else class="text-muted text-center mb-0 py-4">
        Search to fetch insurance base value data.
      </p>
    </div>
  </b-card>
</template>

<script>
import { BCard, BRow, BCol } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'

const EMPTY_SUMMARY = () => ({
  count: 0,
  total: 0,
  np: { count: 0, total: 0 },
  nc: { count: 0, total: 0 },
})

export default {
  components: {
    BCard,
    BRow,
    BCol,
    VueApexCharts,
  },
  props: {
    insBaseValue: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      summary: EMPTY_SUMMARY(),
      series: [],
      chartOptions: {
        chart: {
          type: 'bar',
          toolbar: { show: true },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            borderRadius: 6,
            dataLabels: { position: 'top' },
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ['#304758'],
          },
          formatter: (val, opts) => {
            if (opts.seriesIndex === 1) return this.formatMoney(val)
            return val
          },
        },
        colors: ['#91b3a5', '#a6c6dd'],
        xaxis: {
          categories: ['NP', 'NC'],
        },
        yaxis: [
          {
            seriesName: 'Count',
            title: { text: 'Count' },
            labels: { formatter: val => Math.round(val) },
          },
          {
            seriesName: 'Total ($)',
            opposite: true,
            title: { text: 'Total ($)' },
            labels: { formatter: val => this.formatMoney(val) },
          },
        ],
        legend: {
          position: 'top',
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val, opts) => {
              if (opts.seriesIndex === 1) return this.formatMoney(val)
              return val
            },
          },
        },
        noData: {
          text: 'No insurance base value data',
        },
      },
    }
  },
  computed: {
    hasData() {
      return this.summary.count > 0 || this.summary.total > 0
    },
  },
  watch: {
    insBaseValue: {
      immediate: true,
      deep: true,
      handler(value) {
        this.updateChart(value)
      },
    },
  },
  methods: {
    normalize(value) {
      if (!value || typeof value !== 'object') return EMPTY_SUMMARY()
      return {
        count: parseInt(value.count, 10) || 0,
        total: parseFloat(value.total) || 0,
        np: {
          count: parseInt(value.np?.count, 10) || 0,
          total: parseFloat(value.np?.total) || 0,
        },
        nc: {
          count: parseInt(value.nc?.count, 10) || 0,
          total: parseFloat(value.nc?.total) || 0,
        },
      }
    },
    formatMoney(val) {
      const n = parseFloat(val) || 0
      return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    },
    updateChart(value) {
      this.summary = this.normalize(value)
      if (!this.hasData) {
        this.series = []
        return
      }
      this.series = [
        {
          name: 'Count',
          data: [this.summary.np.count, this.summary.nc.count],
        },
        {
          name: 'Total ($)',
          data: [this.summary.np.total, this.summary.nc.total],
        },
      ]
    },
  },
}
</script>

<style scoped>
.ins-base-value-component h1 {
  font-size: 1.25rem;
}

.total-badge {
  font-size: 0.9rem;
  font-weight: 600;
  color: #6c757d;
}

.summary-row {
  margin-top: 0.5rem;
}

.summary-box {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.summary-label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.summary-value {
  font-size: 1.25rem;
  color: #3c231d;
  line-height: 1.3;
}

.summary-sub {
  font-size: 0.85rem;
  color: #495057;
  margin-top: 0.15rem;
}

.ins-base-chart-container {
  min-height: 200px;
}
</style>
